import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Trophy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Define bracket layout per league
const BRACKET_CONFIG = {
  'Junior League': {
    rounds: [
      { name: 'Finals', stages: ['Finals'] },
    ],
  },
  'World Cup': {
    rounds: [
      { name: 'Finals', stages: ['Finals'] },
    ],
  },
  'Champions League': {
    rounds: [
      { name: 'Semi Finals', stages: ['Semi Finals'] },
      { name: 'Finals', stages: ['Finals'] },
    ],
  },
  'Icons League': {
    rounds: [
      { name: 'Playoff', stages: ['Playoff'] },
      { name: 'Semi Finals', stages: ['Semi Finals'] },
      { name: 'Finals', stages: ['Finals'] },
    ],
  },
};

function MatchCard({ match }) {
  if (!match) {
    return (
      <div className="bg-[#0A0F1C] border border-dashed border-white/10 rounded-lg p-4 min-h-[110px] flex items-center justify-center">
        <span className="text-[#6B7280] text-sm italic">TBD</span>
      </div>
    );
  }

  const homeIsPlaceholder = !match.home_team.id;
  const awayIsPlaceholder = !match.away_team.id;
  const isLive = match.status === 'live';
  const isFinished = match.status === 'finished';
  const homeWon = isFinished && match.home_score > match.away_score;
  const awayWon = isFinished && match.away_score > match.home_score;

  return (
    <div
      data-testid={`bracket-match-${match.id}`}
      className={`bg-[#0A0F1C] border rounded-lg p-3 min-h-[110px] transition-all ${
        isLive
          ? 'border-[#FF3B30] shadow-[0_0_15px_rgba(255,59,48,0.3)]'
          : 'border-white/10 hover:border-[#D4AF37]/40'
      }`}
    >
      {isLive && (
        <div className="flex items-center gap-1 mb-1">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulse-live" />
          <span className="text-[#FF3B30] text-[10px] font-bold uppercase tracking-wider">Live</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 py-1">
        <span
          className={`text-sm font-semibold truncate ${
            homeIsPlaceholder ? 'text-[#9BA3AF] italic' : homeWon ? 'text-[#D4AF37]' : 'text-white'
          }`}
        >
          {match.home_team.name}
        </span>
        <span className={`font-mono font-bold text-lg ${homeWon ? 'text-[#D4AF37]' : 'text-white'}`}>
          {match.home_score}
        </span>
      </div>
      <div className="border-t border-white/5 my-1" />
      <div className="flex items-center justify-between gap-2 py-1">
        <span
          className={`text-sm font-semibold truncate ${
            awayIsPlaceholder ? 'text-[#9BA3AF] italic' : awayWon ? 'text-[#D4AF37]' : 'text-white'
          }`}
        >
          {match.away_team.name}
        </span>
        <span className={`font-mono font-bold text-lg ${awayWon ? 'text-[#D4AF37]' : 'text-white'}`}>
          {match.away_score}
        </span>
      </div>
      <div className="text-[10px] text-[#6B7280] mt-1 truncate flex items-center justify-between">
        <span>
          {new Date(match.match_date).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Europe/Brussels',
            hour12: false,
          })}
        </span>
        {match.pitch && (
          <span className="text-[#0038B8] font-bold">Pitch {match.pitch}</span>
        )}
      </div>
    </div>
  );
}

export default function KnockoutBracket({ ageGroup }) {
  const [matches, setMatches] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    setInitialLoading(true);
  }, [ageGroup]);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const { data } = await axios.get(`${API}/matches?age_group=${encodeURIComponent(ageGroup)}`);
        setMatches(data);
      } catch (error) {
        console.error('Failed to fetch matches:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchMatches();
    const interval = setInterval(fetchMatches, 15000);
    return () => clearInterval(interval);
  }, [ageGroup]);

  const config = BRACKET_CONFIG[ageGroup] || BRACKET_CONFIG['Junior League'];

  // Group matches by stage
  const matchesByStage = {};
  matches.forEach((m) => {
    if (!matchesByStage[m.stage]) matchesByStage[m.stage] = [];
    matchesByStage[m.stage].push(m);
  });

  // Sort each stage by match_date
  Object.keys(matchesByStage).forEach((s) => {
    matchesByStage[s].sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
  });

  const hasAnyKnockoutMatch = config.rounds.some((r) =>
    r.stages.some((s) => (matchesByStage[s] || []).length > 0)
  );

  return (
    <Card className="bg-[#131C2F] border border-white/10 p-6" data-testid="knockout-bracket">
      <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
        <Trophy className="w-6 h-6 text-[#D4AF37]" />
        Knockout Bracket - {ageGroup}
      </h3>
      {initialLoading ? (
        <p className="text-[#9BA3AF]">Loading bracket...</p>
      ) : !hasAnyKnockoutMatch ? (
        <p className="text-[#9BA3AF]">No knockout matches scheduled.</p>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-6 items-stretch min-w-max">
            {config.rounds.map((round, ri) => {
              const roundMatches = round.stages.flatMap((s) => matchesByStage[s] || []);
              return (
                <div key={round.name} className="flex flex-col gap-4 min-w-[220px]">
                  <div className="text-center">
                    <span
                      className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${
                        ri === config.rounds.length - 1
                          ? 'bg-[#D4AF37] text-[#0A0F1C]'
                          : 'bg-[#1E2A42] text-[#D4AF37] border border-[#D4AF37]/30'
                      }`}
                    >
                      {round.name}
                    </span>
                  </div>
                  <div
                    className="flex flex-col justify-around flex-1 gap-4"
                    style={{
                      // Center single matches vertically against multi-match columns
                      paddingTop: roundMatches.length === 1 && ri > 0 ? '40px' : 0,
                    }}
                  >
                    {roundMatches.length === 0 ? (
                      <MatchCard match={null} />
                    ) : (
                      roundMatches.map((m) => <MatchCard key={m.id} match={m} />)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}
